import maplibregl, { type LngLatBounds } from "maplibre-gl";
import { useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";

import { VEHICLE_CUSTOM_LAYER_ID } from "../components/MapLibre/mapLayerIds";
import { DecodePath } from "../tools/ItineraryDecoder";
import { GeometryType } from "../types/dtos/api/itinerary/itinerary.dto";
import type { Route } from "../types/Forms";
import type { SVGComponentProps } from "../types/Svgs";

import DestinationIcon from "../image/icons/map/details/DestinationIcon.svg";
import StartPointIcon from "../image/icons/map/details/StartPointIcon";
import { drawnItineraryGeometrySlice, ephemeralStore, itineraryPanelStateSlice, mapReadySlice, useEphemeralSelector, useEphemeralSliceSetter, useMapInstance } from "../store/ephemeral";

/**
 * On reprend la logique du SwitchIcon en version fonction simple
 */
function getModeIconJSX( mode: "startPoint" | "arrivalPoint", props: SVGComponentProps ) {
    switch ( mode ) {
        case "startPoint":
            return <StartPointIcon {...props} />;

        case "arrivalPoint":
            return (
                <img
                    alt="ArrivalPoint"
                    className="w-[20px]"
                    src={DestinationIcon}
                />
            );

        default:
            return null;
    }
}

/**
 * Propriétés attendues par le hook
 */
interface UseMapCarRoutesProps {
    "itineraries":           Route[]; // Liste des itinéraires
    "selectedCarRouteId":    string; // ID de l'itinéraire sélectionné
    "setSelectedCarRouteId": ( id: string ) => void; // Callback pour définir l'itinéraire sélectionné
    "itineraryListHeight":   number; // Hauteur (px ou %) de la liste
    "geometryType"?:         GeometryType; // Type de géométrie
    "legIdForSelected"?:     string; // Optional leg id to bind to the active polyline for navigation correlation
}

/**
 * Hook moderne qui gère l'affichage de plusieurs polylignes (itineraries),
 * la sélection, les marqueurs de départ/arrivée, et le fitBounds
 */
export function useCarItinerary( {
    itineraries,
    selectedCarRouteId,
    setSelectedCarRouteId,
    itineraryListHeight,
    geometryType,
    legIdForSelected,
}: UseMapCarRoutesProps ): void {
    const map = useMapInstance();
    const isMapReady = useEphemeralSelector( mapReadySlice, ( ready ) => ready.isMapReady );
    const isDragging = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.isDragging );
    const setDrawnItineraryGeometry = useEphemeralSliceSetter( drawnItineraryGeometrySlice );

    /*
     * On stocke les éléments qu'on va créer sur la carte
     *   (layers, sources, markers) pour les nettoyer ensuite
     */
    const mapElementsRef = useRef<
        Array<{
            "sourceId"?: string;
            "layerId"?:  string;
            "marker"?:   maplibregl.Marker;
        }>
    >( [] );

    // Pour gérer le "fitBounds" seulement au premier chargement
    const initialLoad = useRef( true );

    // Conserver l'ancienne liste pour comparer
    const previousItinerariesRef = useRef<Route[]>( [] );

    useEffect( () => {
        if ( map === undefined || !isMapReady ) {
            return;
        }

        // ⚠️ Ignorer tous les recalculs de map pendant le drag du panneau
        if ( isDragging ) {
            return;
        }

        // Nettoyage préalable
        const clearMapElements = () => {
            mapElementsRef.current.forEach( ( elem ) => {
                if ( elem.layerId !== undefined && map.getLayer( elem.layerId ) != null ) {
                    map.removeLayer( elem.layerId );
                }
                if ( elem.sourceId !== undefined && map.getSource( elem.sourceId ) != null ) {
                    map.removeSource( elem.sourceId );
                }
                if ( elem.marker != null ) {
                    elem.marker.remove();
                }
            } );
            mapElementsRef.current = [];
        };

        clearMapElements();

        // Si aucun itinéraire, on s'arrête là
        if ( itineraries.length === 0 ) {
            return;
        }

        /*
         * ✅ CORRECTION: Toujours utiliser la valeur calculée basée sur itineraryListHeight
         * Le DOM physique (offsetHeight) est désynchronisé après le drag
         */
        const containerHeight = ( itineraryListHeight * window.innerHeight ) / 100;

        const topContainerHeight = 160;
        const bottomContainerHeight = containerHeight;

        // On va collecter les limites (bounds)
        let bounds: LngLatBounds | null = null;

        // Séparer l'itinéraire sélectionné des autres
        const nonSelectedItineraries = itineraries.filter( ( it ) => it.id !== selectedCarRouteId );
        const selectedCarRouteIdObject = itineraries.find( ( it ) => it.id === selectedCarRouteId );

        /**
         * Ajoute un itinéraire (Route) sur la carte
         * renvoie l'id de la couche pour gérer ensuite les events
         */
        const addPolylineLayer = ( itinerary: Route, color: string, selected: boolean ) => {
            if ( itinerary.line === undefined ) {
                return null;
            }

            // Décodage en lat/lng
            const decodedPoints = DecodePath( itinerary.line, geometryType ?? GeometryType.THREE_DIMENSIONAL );
            if ( decodedPoints.length === 0 ) {
                return null;
            }

            // Construction d'une Feature (LineString)
            const sourceId = `source_${ itinerary.id }_${ Date.now() }_${ Math.random() }`;
            const layerId = selected
                ? "car_layer"
                : `layer_${ itinerary.id }_${ Date.now() }_${ Math.random() }`;

            const geojsonLine: GeoJSON.GeoJSON = {
                "type":     "Feature",
                "geometry": {
                    "type":        "LineString",
                    "coordinates": decodedPoints.map( ( p ) => [ p.lng, p.lat ] ),
                },
                "properties": { "routeId": itinerary.id }, // pour y accéder dans les events
            };

            // Ajout de la source
            map.addSource( sourceId, {
                "type": "geojson",
                "data": geojsonLine,
            } );

            // Ajout d'un layer (sous la couche bus pour garder les bus au premier plan)
            const beforeId = map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) != null ? VEHICLE_CUSTOM_LAYER_ID : undefined;
            map.addLayer( {
                "id":     layerId,
                "type":   "line",
                "source": sourceId,
                "paint":  {
                    "line-color":   color,
                    "line-width":   5,
                    "line-opacity": 1,
                },
            }, beforeId );

            // On stocke pour cleanup
            mapElementsRef.current.push( {
                sourceId,
                layerId,
            } );

            // Calcul des bounds
            decodedPoints.forEach( ( pt ) => {
                if ( bounds == null ) {
                    bounds = new maplibregl.LngLatBounds( [ pt.lng, pt.lat ], [ pt.lng, pt.lat ] );
                } else {
                    bounds.extend( [ pt.lng, pt.lat ] );
                }
            } );

            // Marqueur départ
            const startIconHtml = ReactDOMServer.renderToStaticMarkup( getModeIconJSX( "startPoint", {
                "size":           [ 24, 24 ],
                "primaryColor":   color,
                "secondaryColor": "#fff",
            } ) );
            const startEl = document.createElement( "div" );
            startEl.innerHTML = startIconHtml;
            const startMarker = new maplibregl.Marker( { "element": startEl } ).
                setLngLat( [ decodedPoints[0].lng, decodedPoints[0].lat ] ).
                addTo( map );
            mapElementsRef.current.push( { "marker": startMarker } );

            // Marqueur arrivée
            const endIconHtml = ReactDOMServer.renderToStaticMarkup( getModeIconJSX( "arrivalPoint", {
                "size":           [ 24, 24 ],
                "primaryColor":   "#112e66",
                "secondaryColor": "#fff",
            } ) );
            const endEl = document.createElement( "div" );
            endEl.innerHTML = endIconHtml;
            const endMarker = new maplibregl.Marker( { "element": endEl } ).
                setLngLat( [
                    decodedPoints[decodedPoints.length - 1].lng,
                    decodedPoints[decodedPoints.length - 1].lat,
                ] ).
                addTo( map );
            mapElementsRef.current.push( { "marker": endMarker } );

            /*
             * Rendre la couche cliquable
             * On intercepte l'event click sur le layerId
             */
            map.on( "click", layerId, ( e ) => {
                /*
                 * Vérifier qu'on a bien cliqué sur la feature
                 * NB: e.features ? => un tableau de features
                 */
                if ( e.features != null && e.features.length > 0 ) {
                    const feature = e.features[0];
                    const routeId = feature.properties?.routeId as string | undefined;
                    if ( routeId !== undefined ) {
                        setSelectedCarRouteId( routeId );
                    }
                }
            } );

            map.on( "mousemove", layerId, ( e ) => {
                if ( e.features != null && e.features.length > 0 ) {
                    map.getCanvas().style.cursor = "pointer";
                } else {
                    map.getCanvas().style.cursor = "grab";
                }
            } );

            return layerId;
        };

        // 1) Ajouter d'abord les itinéraires "non-sélectionnés"
        nonSelectedItineraries.forEach( ( it ) => {
            // Couleur par défaut si c'est un retour
            const color = it.isReturn !== null ? "#538255" : "#8d8c90";
            addPolylineLayer( it, color, false );
        } );

        // 2) Ajouter l’itinéraire sélectionné
        if ( selectedCarRouteIdObject != null ) {
            // Couche "shadow"
            const shadowId = addPolylineLayer( selectedCarRouteIdObject, "#000000", false );

            // On modifie l'opacité et l'épaisseur de la ligne, si on veut simuler un "contour"
            if ( shadowId !== null ) {
                // Pour modifier a posteriori :
                map.setPaintProperty( shadowId, "line-opacity", 0.2 );
                map.setPaintProperty( shadowId, "line-width", 7 );
            }

            // Couche "couleur visible" sur le dessus
            addPolylineLayer( selectedCarRouteIdObject, "#11D39C", true );

            // Update drawn itinerary geometry for LineFollower
            if ( selectedCarRouteIdObject.line !== undefined ) {
                const decodedPoints = DecodePath(
                    selectedCarRouteIdObject.line,
                    geometryType ?? GeometryType.THREE_DIMENSIONAL,
                );

                if ( decodedPoints.length > 0 ) {
                    // Build drawn itinerary geometry for LineFollower
                    setDrawnItineraryGeometry( {
                        "id":        selectedCarRouteIdObject.id,
                        "polylines": [
                            {
                                "id":          "car_route",
                                "legId":       legIdForSelected,
                                "type":        "car",
                                "coordinates": decodedPoints.map( ( p ) => [ p.lng, p.lat ] as [number, number] ),
                            },
                        ],
                    } );
                }
            }
        } else {
            // Clear drawn geometry when no itinerary is selected
            setDrawnItineraryGeometry( null );
        }

        /**
         * Gestion du fitBounds :
         *  - Au 1er chargement
         *  - Ou si la liste d’itinéraires a changé (taille ou ids)
         */
        const hasItineraryListChanged =
            itineraries.length !== previousItinerariesRef.current.length ||
            !itineraries.every( ( it, i ) => it.id === previousItinerariesRef.current[i]?.id );

        if ( bounds !== null ) {
            if ( initialLoad.current || hasItineraryListChanged ) {
                map.fitBounds( bounds, {
                    "padding": {
                        "top":    topContainerHeight,
                        "bottom": bottomContainerHeight,
                        "left":   0,
                        "right":  0,
                    },
                    "maxZoom":  15,
                    "duration": 1000,
                } );

                // On force le resize après un délai si besoin
                setTimeout( () => {
                    map.resize();
                }, 300 );

                initialLoad.current = false;
            }
        }

        // Mettre à jour le ref
        previousItinerariesRef.current = itineraries;

        // Nettoyage
        return () => {
            if ( ephemeralStore.peek( itineraryPanelStateSlice ).isDragging ) {
                return;
            }

            // Retire les listeners sur la map pour éviter fuites
            mapElementsRef.current.forEach( ( elem ) => {
                if ( elem.layerId !== undefined ) {
                    map.off( "click", elem.layerId, () => {
                    } );
                }
            } );
            clearMapElements();
        };
    }, [
        map,
        isMapReady,
        itineraries,
        selectedCarRouteId,
        setSelectedCarRouteId,
        itineraryListHeight,
        geometryType,
        setDrawnItineraryGeometry,
        isDragging,
        legIdForSelected,
    ] );
}
